import React from 'react';

import { FooterStoryblok } from '../../types/storyblok';
import { Type } from '../../base/Type/Type';
import { Box } from '../../base/Box/Box';
import Link from '../../base/Link/Link';
import { StoryblokImage } from '../../base/StoryblokImage/StoryblokImage';
import isDarkColour from '../../utils/isDarkColour';
import { SubLink } from './SubLink';

const Footer = ({
  footerLinks,
  subscribeHeading,
  subscribeText,
  subscribeButtonText,
  subscribeUrl,
  logo,
  logoLight,
  socialMediaLinks,
  copyrightText,
  legalLinks,
  colourScheme = 'white',
  overlayFooterOverBkg,
}: FooterStoryblok) => {
  const year = new Date();

  return (
    <Box
      as="footer"
      className=" border-t pt-5"
      colourScheme={colourScheme}
      bkg
      border
      overlayOverBkg={overlayFooterOverBkg}
    >
      {/* Footer nav and form */}
      <Box as="div" blockSpacing>
        <Box
          as="div"
          className="mx-auto flex max-w-standard flex-wrap gap-7 lg:flex-nowrap"
        >
          {/* Footer nav */}
          <Box
            as="div"
            className="lg-w-2/3 grid w-full grid-cols-2 gap-7 xs:grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-5"
          >
            {footerLinks?.map((list) => (
              <Box as="div" className="mb-10lg:mb-0">
                <Box
                  as="div"
                  className="ff-type mb-4 inline-block text-h4 font-light !text-ffMidGrey"
                >
                  {list?.title}
                </Box>
                {
                  <Box as="ul" className="flex list-none flex-col gap-4">
                    {list?.subLinks?.map(({ link, linkTitle }) => (
                      <SubLink link={link} linkTitle={linkTitle} />
                    ))}
                  </Box>
                }
              </Box>
            ))}
          </Box>
          {/* FF email signup */}
          <Box as="div" className="mb-10 w-full lg:mb-0 lg:w-1/3">
            <Type as="h2" className="mb-4 text-h4 font-light !text-ffMidGrey">
              {subscribeHeading}
            </Type>
            <Box as="div">
              <Type as="p" className="mt-4 text-p1">
                {subscribeText}
              </Type>
              <Link href={subscribeUrl} variant="primary" className="mt-7">
                {subscribeButtonText}
              </Link>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* FF logo and social media links */}
      <Box as="div" blockSpacing>
        <Box
          as="div"
          className="md:mt-200 mx-auto mb-6 mt-5 flex max-w-standard items-end justify-between sm:mt-10 lg:mt-30"
        >
          <Box as="div">
            <StoryblokImage
              src={isDarkColour(colourScheme) ? logoLight : logo}
              maxWidth={400}
              mode="contain"
              alt="Founders Factory logo"
              className="h-8 lg:mb-8 !w-[80px]"
            />
          </Box>
          <Box as="div" className="flex items-end gap-5 lg:mb-8">
            {socialMediaLinks?.map((link) => (
              <Link href={link?.url}>
                <StoryblokImage
                  src={
                    isDarkColour(colourScheme) ? link?.logoLight : link?.logo
                  }
                  maxWidth={25}
                  mode="responsive"
                  alt={link?.name}
                  className="h-5"
                />
              </Link>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Legal details and links */}
      <Box as="div" className="sticky border-t" blockSpacing>
        <Box as="div" className="container mx-auto max-w-standard">
          <Box
            as="div"
            className="flex flex-row flex-wrap justify-between gap-7 py-7 sm:flex-row"
          >
            <Type as="p" className="text-p4">
              © {year.getFullYear()} {copyrightText}
            </Type>
            <Box as="div" className="flex flex-row flex-wrap gap-6">
              {legalLinks?.map((link) => (
                <Link href={link?.link} className="text-p4 underline">
                  {link?.linkTitle}
                </Link>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Footer;
